import { ActivatedRoute, Router } from '@angular/router';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, Injector, ElementRef, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ParametreContextuelDto } from '../../../models/parametre-contextuel-dto.model';
import { PersonnePhysiqueDto } from '../../../models/personne-physique-dto.model';

@Component({
  selector: 'app-pop-up-contact',
  templateUrl: './pop-up-contact.component.html'
})
export class PopUpContactComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('template') template: TemplateRef<any>;
  @ViewChild('emailInput') emailInput: ElementRef;

  @Output() popUpEvent = new EventEmitter<UntypedFormGroup>();
  @Output() oncloseEvent = new EventEmitter<string>();

  @Input() isRecapitulatif: boolean;
  @Input() isUpdateMailNumber: boolean;
  @Input() personne: PersonnePhysiqueDto;
  @Input() parametreContextuel: ParametreContextuelDto;
  @Input() dateFin: string;
  @Input() isProfil: boolean;
  @Input() modalRef: BsModalRef;
  @Input() title: string;

  public onClose: Subject<string>;
  public isDemandeur = false;
  validateDisabled = true;
  translate: TranslateService;
  isSingleDuplication: boolean;
  isAllDuplication: boolean;
  isCreationMandataire: boolean;
  private email: string;
  private phoneNumber: string;

  popUpForm: UntypedFormGroup = this.formBuilder.group({
    email: [
      '',
      [
        //(?!.*\.\.) Interdiction des "." successifs.
        // (?=.{1,255}$) Limitation de la longueur totale en raison de la taille max autorisé en BDD.
        Validators.pattern(/^(?!.*\.\.)(?=.{1,255}$)[a-z0-9._%+-]{1,63}@([a-z0-9.-]{1,63})\.([a-z]{1,63})$/i),
        Validators.required,
      ],
    ],
    telephone: [
      '',
      [
        Validators.maxLength(16),
        Validators.pattern(/^(?:(?:\+|00)[0-9]{2}[\s.-]{0,3}(?:\(0\)[\s.-]{0,3})?|0)[1-9](?:(?:[\s.-]?\d{2}){4}|\d{2}(?:[\s.-]?\d{3}){2})$/),
      ],
    ],
  });

  router: Router;

  subsCriptions: Subscription;
  titleAdministrateur = 'Coordonnées de contact';
  titleRecapitulatif = 'Souhaitez-vous recevoir un mail de confirmation?';
  texteAdministrateur: string;
  texteButtonRecapitulatif = 'Passer cette étape';
  texteRecapitulatif =
    'En tant que Demandeur, si vous souhaitez recevoir une notification vous pouvez ' +
    'renseigner votre adresse de messagerie et cliquer sur le bouton "Valider".' +
    'Sinon cliquer sur le bouton "Passer cette étape". Votre demande d`habilitation est terminée et sera envoyée et traitée même si vous passez cette étape.';

  isButtonTouched = false;

  constructor(
    private readonly injector: Injector,
    private readonly modalService: BsModalService,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder
  ) {
    this.translate = this.injector.get<TranslateService>(TranslateService);
    this.router = this.injector.get<Router>(Router);
    this.route.queryParams.subscribe((params) => {
      this.isSingleDuplication = params.isDuplicateSingleHabilitation === 'true' ?? false;
      this.isAllDuplication = params.isDuplicateAllHabilitation === 'true' ?? false;
      this.isCreationMandataire = params.isCreationMandataire === 'true' ?? false;
    });
  }

  ngOnInit(): void {
    // init variables
    this.email = this.personne?.email || (this.modalService.config.initialState as any)?.['personne']?.email;
    this.phoneNumber = this.personne?.numeroDeTel || (this.modalService.config.initialState as any)?.['personne']?.telephone;
    this.configTexts();

    // set form values in 3 modes:
    // - recapitulatif
    // - update mail from profile view
    // - user trying to authorize himself (email or phone number)
    if (this.isRecapitulatif
      || this.isUpdateMailNumber
      || this.email
      || this.phoneNumber) {
      this.setFormValues();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.emailInput?.nativeElement.focus(), 500);
  }

  ngOnDestroy(): void {
    if (this.isRecapitulatif && !this.isButtonTouched) {
      this.popUpEvent.emit(this.popUpForm);
      this.oncloseEvent.emit('isNotValidated');
    }
  }

  setFormValues(): void {
    if (this.email) {
      const f = this.popUpForm.get('email');
      f.setValue(this.email);
      f.markAsDirty();
    }
    if (this.phoneNumber) {
      const f = this.popUpForm.get('telephone');
      f.setValue(this.phoneNumber);
      f.markAsDirty();
    }
  }

  openModal(): BsModalRef {
    this.modalRef = this.modalService.show(PopUpContactComponent);
    return this.modalRef;
  }

  validerPopUp() {
    // skip valider popup if form is invalid
    if (this.popUpForm.invalid) {
      return;
    }

    this.isButtonTouched = true;
    this.popUpEvent.emit(this.popUpForm);
    if (this.isRecapitulatif) {
      this.oncloseEvent.emit('isValide');
    }
    this.modalRef.hide();
  }

  annulerPopUp(): void {
    this.isButtonTouched = true;
    if (this.isRecapitulatif) {
      this.popUpEvent.emit(this.popUpForm);
      this.oncloseEvent.emit('isAnnuler');
    }

    this.modalRef.hide();
  }

  configTexts() {
    this.translate.get('last').subscribe(() => {
      if (this.isSingleDuplication || this.isAllDuplication) {
        this.texteAdministrateur = this.translate.instant(
          `habilitation.administrateur.${this.router.url
            ?.split('/')[4]
            ?.split('?')[0]
            ?.toUpperCase()}.textAdministrateur`
        );

      } else if (this.isCreationMandataire) {
        this.texteAdministrateur = this.translate.instant(`habilitation.administrateur.MANDATAIRE.textAdministrateur`);

      } else if (this.isUpdateMailNumber && !this.isProfil) {
        this.texteAdministrateur = this.translate.instant(`habilitation.administrateur.CL.textUpdate`);
        this.titleAdministrateur = this.translate.instant(`habilitation.administrateur.CL.titleUpdate`);

      } else if (this.isUpdateMailNumber && this.isProfil) {
        this.texteAdministrateur = this.translate.instant(`profil.textUpdate`);
        this.titleAdministrateur = this.translate.instant(`habilitation.administrateur.CL.titleUpdate`);

      } else {
        this.texteAdministrateur = this.translate.instant(
          `habilitation.administrateur.${this.router.url
            ?.split('/')[2]
            ?.replace('-', '_')
            ?.toUpperCase()}.textAdministrateur`
        );
      }
    });
  }

}
